import React, { useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../services/api';
import { GenerateCertificateModal } from '../components/GenerateCertificateModal';
import { CertificationImportModal } from '../components/CertificationImportModal';
import { useAuth } from '../contexts/AuthContext';

type CampaignSummary = { id: string; nome: string; criadoEm?: string };
type CertTemplate = { id: string; title: string; body: string; sequence?: number };
type ChatCertContact = {
	contactName: string;
	contactPhone: string;
	contactEmail?: string | null;
	contactDocument?: string | null;
	contactAddress?: string | null;
	contactProcess?: string | null;
	contactMandate?: string | null;
	contactCategory?: string | null;
	contactNotes?: string | null;
	mandateId?: string | null;
	mandateRecipientName?: string | null;
	mandateAddress?: string | null;
	mandateStatus?: string | null;
	mandateCaseNumber?: string | null;
	mandateType?: string | null;
	sentAt?: string | null;
	createdAt?: string | null;
	conversationStartedAt?: string | null;
	conversationLastMessageAt?: string | null;
	conversationMessageCount?: number | null;
	conversationSummary?: string | null;
	conversationTranscript?: string | null;
	campaignId?: string | null;
	campaignName?: string | null;
	campaignMessageId?: string | null;
};

type ChatCertificateDraft = {
	contactName: string;
	contactPhone: string;
	contactEmail?: string | null;
	contactDocument?: string | null;
	contactAddress?: string | null;
	contactProcess?: string | null;
	contactMandate?: string | null;
	contactCategory?: string | null;
	contactNotes?: string | null;
	mandateId?: string | null;
	mandateRecipientName?: string | null;
	mandateAddress?: string | null;
	mandateStatus?: string | null;
	mandateCaseNumber?: string | null;
	mandateType?: string | null;
	sentAt?: string | null;
	createdAt?: string | null;
	conversationStartedAt?: string | null;
	conversationLastMessageAt?: string | null;
	conversationMessageCount?: number | null;
	conversationSummary?: string | null;
	conversationTranscript?: string | null;
	campaignId?: string | null;
	campaignName?: string | null;
	campaignMessageId?: string | null;
};

const CAMPAIGNS_CACHE_TTL_MS = 2 * 60 * 1000;
const CERT_TEMPLATES_STORAGE_KEY_PREFIX = 'certification-templates-v1';
let campaignsCache: CampaignSummary[] | null = null;
let campaignsCacheAt = 0;

const safeStorageSegment = (value?: string | null) => {
	if (!value) return 'none';
	return value.replace(/[^a-zA-Z0-9_-]/g, '_');
};

const repairMojibake = (value: string) => {
	try {
		return decodeURIComponent(escape(value));
	} catch {
		return value;
	}
};

const TEMPLATE_SEQUENCE_PREFIX_RE = /^modelo\s+\d+\s*(?:[-–—])\s*/i;

const stripTemplateSequenceLabel = (title: string) =>
	title
		.replace(TEMPLATE_SEQUENCE_PREFIX_RE, '')
		.replace(/^modelo\s+\d+\s*/i, '')
		.replace(/^[^\p{L}\p{N}]+/u, '')
		.trim();

const formatTemplateSequenceLabel = (sequence: number) => `MODELO ${String(sequence).padStart(2, '0')}`;

const formatTemplateDisplayTitle = (template: CertTemplate) => {
	const sequence = template.sequence && template.sequence > 0 ? template.sequence : 1;
	const baseTitle = stripTemplateSequenceLabel(template.title);
	return `${formatTemplateSequenceLabel(sequence)} - ${baseTitle}`;
};

const normalizeTemplate = (template: CertTemplate, index: number): CertTemplate => ({
	...template,
	title: stripTemplateSequenceLabel(repairMojibake(template.title)),
	body: repairMojibake(template.body),
	sequence: Number.isFinite(template.sequence) && (template.sequence || 0) > 0 ? template.sequence : index + 1,
});

const normalizeTemplates = (items: CertTemplate[]) => items.map((template, index) => normalizeTemplate(template, index));

const resequenceTemplates = (items: CertTemplate[]) =>
	items.map((template, index) => ({
		...template,
		title: stripTemplateSequenceLabel(repairMojibake(template.title)),
		body: repairMojibake(template.body),
		sequence: index + 1,
	}));

const decodeTokenPayload = (token: string) => {
	try {
		const payload = token.split('.')[1];
		if (!payload) return null;
		const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
		const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
		const decoded = atob(padded);
		return JSON.parse(decoded);
	} catch {
		return null;
	}
};

const toInputDateValue = (value?: string | null) => {
	if (!value) return '';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '';
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

const toInputTimeValue = (value?: string | null) => {
	if (!value) return '';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '';
	const hours = String(date.getHours()).padStart(2, '0');
	const minutes = String(date.getMinutes()).padStart(2, '0');
	return `${hours}:${minutes}`;
};

const mergeDateAndTime = (dateValue: string, timeValue: string, fallback?: string | null) => {
	if (!dateValue) {
		return fallback || null;
	}

	const safeTime = timeValue || '00:00';
	const merged = new Date(`${dateValue}T${safeTime}:00`);
	if (Number.isNaN(merged.getTime())) {
		return fallback || null;
	}

	return merged.toISOString();
};

const DEFAULT_CERT_TEMPLATES: CertTemplate[] = [
	{
		id: '1',
		sequence: 1,
		title: 'Modelo 01 - Virtual positiva',
		body: `Certifico que, em cumprimento ao mandado em tela, por meio virtual, nos termos da Resolução CNJ nº 354, de 19/11/2020, da Portaria GC nº 34, de 2 de março de 2021, e da decisão proferida no PA SEI 016466/2020 do TJDFT, procedi à {{tipo_de_mandado}} ordenada. Após a confirmação da identidade do destinatário do ato, enviei, por meio do aplicativo WhatsApp abaixo citado, a cópia do mandado. Em seguida, foi explicado o teor da ordem judicial e questionada a parte acerca da existência de eventuais dúvidas. Assim, havendo confirmação de recebimento e de leitura dos documentos.

Destinatário: {{nome}},
Data: {{data}}
Telefone: {{telefone}}`,
	},
	{
		id: '2',
		sequence: 2,
		title: 'Modelo 02 - Virtual positiva com ligação',
		body: `Certifico que, em cumprimento ao mandado em tela, por meio virtual, nos termos da Resolução CNJ nº 354, de 19/11/2020, da Portaria GC nº 34, de 2 de março de 2021, e da decisão proferida no PA SEI 016466/2020 do TJDFT, procedi à {{tipo_de_mandado}} ordenada. Inicialmente, realizei contato telefônico com a parte e, após a confirmação da identidade do destinatário do ato, enviei, por meio do aplicativo WhatsApp abaixo citado, a cópia do mandado. Em seguida, foi explicado o teor da ordem judicial e questionada a parte acerca da existência de eventuais dúvidas. Assim, havendo confirmação de recebimento e de leitura dos documentos, dou por realizado o ato.

Destinatário: {{nome}},
Data: {{data}}
Telefone: {{telefone}}`,
	},
	{
		id: '3',
		sequence: 3,
		title: 'Modelo 03 - Por WhatsApp com diligência pessoal',
		body: `Certifico que, em cumprimento ao mandado em tela, após ter me dirigido ao endereço constante do mandado e não ter obtido encontrar o destinatário da ordem, realizei o ato por meio virtual (Resolução CNJ nº 354 de 19/11/2020, Portaria GC 34, de 2 de março de 2021, e decisão proferida no PA SEI 016466/2020 do TJDFT) procedendo à {{tipo_de_mandado}} ordenada. Primeiramente, enviei por meio do aplicativo WhatsApp abaixo citado a cópia do mandado e, após, por meio de uma conversa em que foi explicado o mandado e retiradas eventuais dúvidas. Tudo se deu após confirmação da identidade do destinatário do ato. Assim, com confirmação de recebimento e de leitura desses documentos, dou por realizado o ato.

Destinatário: {{nome}},
Data: {{data}}
Telefone: {{telefone}}`,
	},
	{
		id: '4',
		sequence: 4,
		title: 'Modelo 04 - Negativa com ordem de cumprimento virtual',
		body: `Certifico e dou fé que, em cumprimento ao r. mandado, COM ORDEM PARA CUMPRIMENTO VIRTUAL PELO NÚMERO {{telefone}}, estabeleci contato (ligação e WhatsApp), mas não houve resposta/não fui atendido. Realizei pesquisas junto ao Sistema CREDILINK/SINDOJUS para levantamento de meios de contato com a parte, todavia, sem êxito. Desta feita, deixo de proceder à {{tipo_de_mandado}} ordenada. Sem prazo para novas tentativas, recolho o mandado.

Destinatário: {{nome}},
Data: {{data}}
Telefone: {{telefone}}`,
	},
	{
		id: '5',
		sequence: 5,
		title: 'Modelo 05 - Negativa com ato praticado por outro',
		body: `Certifico e dou fé que, em cumprimento ao r. mandado, em {{data}}, dirigi-me ao endereço constante do mandado, onde não procedi à {{tipo_de_mandado}} de {{nome}}, {{telefone}}, visto que na(s) diligência(s) não _______________.

Destinatário: {{nome}},
Data: {{data}}
Telefone: {{telefone}}`,
	},
];

export const CertificationPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
	const { user } = useAuth();
	const location = useLocation();
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState<'certificar' | 'modelos'>('certificar');
	const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
	const [loadingCampaigns, setLoadingCampaigns] = useState(false);
	const [loadingOlderCampaigns, setLoadingOlderCampaigns] = useState(false);
	const [hasOlderCampaigns, setHasOlderCampaigns] = useState(false);

	const [templates, setTemplates] = useState<CertTemplate[]>(DEFAULT_CERT_TEMPLATES);
	const [templatesLoaded, setTemplatesLoaded] = useState(false);
	const [showTemplateModal, setShowTemplateModal] = useState(false);
	const [editing, setEditing] = useState<CertTemplate | null>(null);
	const [modalTitle, setModalTitle] = useState('');
	const [modalBody, setModalBody] = useState('');
	const [isCertificationImportModalOpen, setIsCertificationImportModalOpen] = useState(false);

	const [openedCampaign, setOpenedCampaign] = useState<any>(null);
	const [openedMessages, setOpenedMessages] = useState<any[]>([]);
	const [loadingMessages, setLoadingMessages] = useState(false);
	const [selectedTemplateByMsg, setSelectedTemplateByMsg] = useState<Record<string, string>>({});
	const [mandadoTypeByMsg, setMandadoTypeByMsg] = useState<Record<string, string>>({});

	const [showCertModal, setShowCertModal] = useState(false);
	const [certText, setCertText] = useState('');
	const [certTemplate, setCertTemplate] = useState('');
	const [certContext, setCertContext] = useState<{
		contactName?: string;
		contactPhone?: string;
		contactEmail?: string;
		contactDocument?: string;
		contactAddress?: string;
		contactProcess?: string;
		contactMandate?: string;
		contactCategory?: string;
		dateText?: string;
		timeText?: string;
		sentAt?: string;
		mandadoType?: string;
		conversationDateText?: string;
		conversationTimeText?: string;
		conversationDateTimeText?: string;
		conversationSummary?: string;
		conversationTranscript?: string;
		conversationMessageCount?: number;
	}>({});
	const [showChatCertSetupModal, setShowChatCertSetupModal] = useState(false);
	const [chatCertDraft, setChatCertDraft] = useState<ChatCertificateDraft | null>(null);
	const [chatCertMandadoType, setChatCertMandadoType] = useState('');
	const [chatCertTemplateId, setChatCertTemplateId] = useState('');
	const [chatCertSentDate, setChatCertSentDate] = useState('');
	const [chatCertSentTime, setChatCertSentTime] = useState('');
	const [chatCertCreatedAt, setChatCertCreatedAt] = useState<string | null>(null);
	const cameFromChatRef = React.useRef(false);

	const certTemplatesStorageKey = useMemo(() => {
		const token = localStorage.getItem('auth_token');
		const tokenPayload = token ? decodeTokenPayload(token) : null;
		const userId = user?.id || tokenPayload?.id || tokenPayload?.userId || tokenPayload?.sub || 'anonymous';
		const tenantId = user?.tenantId || tokenPayload?.tenantId || localStorage.getItem('selected_tenant_id') || 'none';
		return `${CERT_TEMPLATES_STORAGE_KEY_PREFIX}:${safeStorageSegment(tenantId)}:${safeStorageSegment(userId)}`;
	}, [user?.id, user?.tenantId]);

	useEffect(() => {
		const state = location.state as { fromChat?: ChatCertContact; fromCampaignContact?: ChatCertContact } | null;
		const incomingContact = state?.fromCampaignContact || state?.fromChat;
		if (incomingContact?.contactName) {
			cameFromChatRef.current = true;
			setChatCertDraft({ ...incomingContact });
			setChatCertMandadoType(incomingContact.contactMandate || incomingContact.mandateType || '');
			setChatCertTemplateId('');
			const certificateCreatedAt = new Date().toISOString();
			setChatCertCreatedAt(certificateCreatedAt);
			setChatCertSentDate(toInputDateValue(certificateCreatedAt));
			setChatCertSentTime(toInputTimeValue(certificateCreatedAt));
			setShowChatCertSetupModal(true);
			setActiveTab('certificar');
		}
	}, [location.state]);

	useEffect(() => {
		if (editing) {
			setModalTitle(stripTemplateSequenceLabel(editing.title));
			setModalBody(editing.body);
		} else {
			setModalTitle('');
			setModalBody('');
		}
	}, [editing]);

	useEffect(() => {
		setTemplatesLoaded(false);
		try {
			const raw = localStorage.getItem(certTemplatesStorageKey);
			if (!raw) {
				setTemplates(normalizeTemplates(DEFAULT_CERT_TEMPLATES));
				setTemplatesLoaded(true);
				return;
			}

			const parsed = JSON.parse(raw);
			if (Array.isArray(parsed)) {
				setTemplates(parsed.length > 0 ? normalizeTemplates(parsed) : normalizeTemplates(DEFAULT_CERT_TEMPLATES));
			} else {
				setTemplates(normalizeTemplates(DEFAULT_CERT_TEMPLATES));
			}
		} catch (error) {
			console.error('Erro ao carregar modelos da certificação:', error);
			setTemplates(normalizeTemplates(DEFAULT_CERT_TEMPLATES));
		} finally {
			setTemplatesLoaded(true);
		}
	}, [certTemplatesStorageKey]);

	useEffect(() => {
		if (!templatesLoaded) return;
		try {
			localStorage.setItem(certTemplatesStorageKey, JSON.stringify(templates));
		} catch (error) {
			console.error('Erro ao salvar modelos da certificação:', error);
		}
	}, [templates, templatesLoaded, certTemplatesStorageKey]);

	const authenticatedFetch = useCallback(async (url: string, options: RequestInit = {}) => {
		const token = localStorage.getItem('auth_token');
		const selectedTenantId = localStorage.getItem('superadmin_selected_tenant') || localStorage.getItem('selected_tenant_id');
		const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
		const headers: HeadersInit = {
			...options.headers,
		};

		if (!isFormData && !(headers as Record<string, string>)['Content-Type']) {
			(headers as Record<string, string>)['Content-Type'] = 'application/json';
		}

		if (token) {
			(headers as Record<string, string>).Authorization = `Bearer ${token}`;
		}

		if (selectedTenantId && !(headers as Record<string, string>)['X-Tenant-Id']) {
			(headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
		}

		return fetch(buildApiUrl(url), {
			...options,
			headers,
		});
	}, []);

	const fetchCampaigns = useCallback(async (forceRefresh = false): Promise<CampaignSummary[]> => {
		const now = Date.now();
		const canUseCache = !forceRefresh && campaignsCache && now - campaignsCacheAt < CAMPAIGNS_CACHE_TTL_MS;

		if (canUseCache) {
			return campaignsCache ?? [];
		}

		const resp = await authenticatedFetch('/campaigns');
		if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

		const data = await resp.json();
		const list = data.campaigns || [];

		const mapped = list.map((c: any) => ({
			id: c.id,
			nome: c.nome,
			criadoEm: c.criadoEm || c.criado_em || c.createdAt || c.created_at || c.criadoAt,
		}));

		mapped.sort((a: any, b: any) => {
			const ta = a.criadoEm ? new Date(a.criadoEm).getTime() : 0;
			const tb = b.criadoEm ? new Date(b.criadoEm).getTime() : 0;
			return tb - ta;
		});

		campaignsCache = mapped as CampaignSummary[];
		campaignsCacheAt = now;
		return campaignsCache ?? [];
	}, [authenticatedFetch]);

	useEffect(() => {
		let cancelled = false;

		const loadInitialCampaigns = async () => {
			try {
				setLoadingCampaigns(true);
				const forceRefreshFromState = Boolean((location.state as { forceRefreshCertification?: boolean } | null)?.forceRefreshCertification);
				const forceRefreshFromStorage = sessionStorage.getItem('certification_import_refresh') === '1';
				const shouldForceRefresh = forceRefreshFromState || forceRefreshFromStorage;

				const mapped = await fetchCampaigns(shouldForceRefresh);

				if (shouldForceRefresh) {
					sessionStorage.removeItem('certification_import_refresh');
					toast.success('Lista de certificação atualizada');
				}

				if (!cancelled) {
					setCampaigns(mapped.slice(0, 10));
					setHasOlderCampaigns(mapped.length > 10);
				}
			} catch (err) {
				console.error('Erro ao carregar campanhas:', err);
				toast.error('Erro ao carregar ações de envio');
			} finally {
				if (!cancelled) setLoadingCampaigns(false);
			}
		};

		loadInitialCampaigns();

		return () => {
			cancelled = true;
		};
	}, [fetchCampaigns, location.state]);

	const handleLoadOlderCampaigns = async () => {
		try {
			setLoadingOlderCampaigns(true);
			const mapped = await fetchCampaigns(true);
			setCampaigns(mapped);
			setHasOlderCampaigns(false);
		} catch (err) {
			console.error('Erro ao carregar ações mais antigas:', err);
			toast.error('Erro ao carregar ações mais antigas');
		} finally {
			setLoadingOlderCampaigns(false);
		}
	};

	const handleCertificationImportSuccess = async () => {
		setIsCertificationImportModalOpen(false);
		sessionStorage.setItem('certification_import_refresh', '1');

		try {
			setLoadingCampaigns(true);
			const mapped = await fetchCampaigns(true);
			setCampaigns(mapped.slice(0, 10));
			setHasOlderCampaigns(mapped.length > 10);
			toast.success('Lista de certificação atualizada');
		} catch (err) {
			console.error('Erro ao atualizar lista após importação:', err);
			toast.error('Importação concluída, mas não foi possível atualizar a lista agora');
		} finally {
			setLoadingCampaigns(false);
		}
	};

	const handleDeleteCampaign = async (campaignId: string) => {
		if (!confirm('Tem certeza que deseja excluir esta lista?')) {
			return;
		}

		try {
			const response = await authenticatedFetch(`/campaigns/${campaignId}`, {
				method: 'DELETE'
			});

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`);
			}

			if (openedCampaign?.id === campaignId) {
				setOpenedCampaign(null);
				setOpenedMessages([]);
			}

			setCampaigns((current) => current.filter((campaign) => campaign.id !== campaignId));
			campaignsCache = campaignsCache?.filter((campaign) => campaign.id !== campaignId) ?? null;
			toast.success('Lista excluída com sucesso!');
		} catch (error) {
			console.error('Erro ao excluir lista:', error);
			toast.error('Erro ao excluir lista');
		}
	};

	const openCampaign = async (id: string) => {
		try {
			setLoadingMessages(true);
			const resp = await authenticatedFetch(`/campaigns/${id}`);
			if (!resp.ok) {
				if (resp.status === 404) {
					setOpenedCampaign(null);
					setOpenedMessages([]);
					toast.error('Esta lista nao existe mais.');
					return;
				}

				throw new Error(`HTTP ${resp.status}`);
			}
			const data = await resp.json();
			setOpenedCampaign(data);
			setOpenedMessages(data.messages || []);
		} catch (err) {
			console.error('Falha ao abrir campanha', err);
			toast.error('Erro ao carregar lista');
		} finally {
			setLoadingMessages(false);
		}
	};

	const setMessageStatus = async (msg: any, newStatus: 'COMPLETED' | 'PENDING') => {
		try {
			const resp = await authenticatedFetch(`/campaigns/${openedCampaign.id}/messages/${msg.id}`, {
				method: 'PATCH',
				body: JSON.stringify({ status: newStatus })
			});

			if (resp.ok) {
				setOpenedMessages((old) => old.map((m) => (m.id === msg.id ? { ...m, status: newStatus } : m)));
			}
		} catch (err) {
			console.error('Erro ao atualizar status', err);
		}
	};

	const generateCertificate = (templateId: string, msg: any, mandadoType = '') => {
		try {
			const tpl = templates.find((t) => t.id === templateId);
			if (!tpl) {
				toast.error('Modelo não encontrado');
				return;
			}

			if (!msg.contactName) {
				toast.error('Nome do destinatario é obrigatório');
				return;
			}

			const sentDateSource = msg.sentAt || msg.criadoEm || msg.updatedAt;
			const sentDate = sentDateSource ? new Date(sentDateSource) : null;
			const formattedDate = sentDate
				? new Intl.DateTimeFormat('pt-BR', {
					day: '2-digit',
					month: '2-digit',
					year: 'numeric',
					hour: '2-digit',
					minute: '2-digit'
				}).format(sentDate)
				: 'Data não disponível';
			const formattedDateOnly = sentDate
				? new Intl.DateTimeFormat('pt-BR', {
					day: '2-digit',
					month: '2-digit',
					year: 'numeric'
				}).format(sentDate)
				: '';
			const formattedTimeOnly = sentDate
				? new Intl.DateTimeFormat('pt-BR', {
					hour: '2-digit',
					minute: '2-digit'
				}).format(sentDate)
				: '';
			const contactPhone = (msg.contactPhone || '').trim() || 'Não informado';
			const conversationDateSource = msg.conversationLastMessageAt || msg.conversationStartedAt || sentDateSource || null;
			const conversationDate = conversationDateSource ? new Date(conversationDateSource) : null;
			const formattedConversationDate = conversationDate
				? new Intl.DateTimeFormat('pt-BR', {
					day: '2-digit',
					month: '2-digit',
					year: 'numeric',
					hour: '2-digit',
					minute: '2-digit'
				}).format(conversationDate)
				: 'Data não disponível';
			const formattedConversationDateOnly = conversationDate
				? new Intl.DateTimeFormat('pt-BR', {
					day: '2-digit',
					month: '2-digit',
					year: 'numeric'
				}).format(conversationDate)
				: '';
			const formattedConversationTimeOnly = conversationDate
				? new Intl.DateTimeFormat('pt-BR', {
					hour: '2-digit',
					minute: '2-digit'
				}).format(conversationDate)
				: '';
			const conversationSummary = (msg.conversationSummary || '').trim();
			const conversationTranscript = (msg.conversationTranscript || '').trim();

			let certificateText = tpl.body;
			certificateText = certificateText.replace(/{{\s*nome\s*}}/gi, msg.contactName.trim());
			certificateText = certificateText.replace(/{{\s*destinatario\s*}}/gi, msg.contactName.trim());
			certificateText = certificateText.replace(/{{\s*telefone\s*}}/gi, contactPhone);
			certificateText = certificateText.replace(/{{\s*email\s*}}/gi, (msg.contactEmail || '').trim());
			certificateText = certificateText.replace(/{{\s*cpf\s*}}/gi, (msg.contactDocument || '').trim());
			certificateText = certificateText.replace(/{{\s*endereco\s*}}/gi, (msg.contactAddress || '').trim());
			certificateText = certificateText.replace(/{{\s*processo\s*}}/gi, (msg.contactProcess || '').trim());
			certificateText = certificateText.replace(/{{\s*mandado\s*}}/gi, (msg.contactMandate || mandadoType || '').trim());
			certificateText = certificateText.replace(/{{\s*ato\s*}}/gi, (msg.contactMandate || mandadoType || '').trim());
			certificateText = certificateText.replace(/{{\s*categoria\s*}}/gi, (msg.contactCategory || '').trim());
			certificateText = certificateText.replace(/{{\s*observacoes\s*}}/gi, (msg.contactNotes || '').trim());
			certificateText = certificateText.replace(/{{\s*data\s*}}/gi, formattedDate);
			certificateText = certificateText.replace(/{{\s*data_envio\s*}}/gi, formattedDateOnly);
			certificateText = certificateText.replace(/{{\s*hora_envio\s*}}/gi, formattedTimeOnly);
			certificateText = certificateText.replace(/{{\s*data_conversa\s*}}/gi, formattedConversationDateOnly);
			certificateText = certificateText.replace(/{{\s*hora_conversa\s*}}/gi, formattedConversationTimeOnly);
			certificateText = certificateText.replace(/{{\s*data_hora_conversa\s*}}/gi, formattedConversationDate);
			certificateText = certificateText.replace(/{{\s*resumo_conversa\s*}}/gi, conversationSummary);
			certificateText = certificateText.replace(/{{\s*historico_conversa\s*}}/gi, conversationTranscript);
			certificateText = certificateText.replace(/{{\s*tipo_de_mandado\s*}}/gi, mandadoType.trim());

			setCertText(certificateText);
			setCertTemplate(formatTemplateDisplayTitle(tpl));
			setCertContext({
				contactName: msg.contactName,
				contactPhone,
				contactEmail: msg.contactEmail,
				contactDocument: msg.contactDocument,
				contactAddress: msg.contactAddress,
				contactProcess: msg.contactProcess,
				contactMandate: msg.contactMandate,
				contactCategory: msg.contactCategory,
				dateText: formattedDate,
				timeText: formattedTimeOnly,
				sentAt: sentDateSource || undefined,
				mandadoType: mandadoType.trim(),
				conversationDateText: formattedConversationDateOnly,
				conversationTimeText: formattedConversationTimeOnly,
				conversationDateTimeText: formattedConversationDate,
				conversationSummary,
				conversationTranscript,
				conversationMessageCount: msg.conversationMessageCount || 0,
			});
			setShowCertModal(true);
		} catch (err) {
			console.error('Erro ao gerar certificação:', err);
			toast.error('Erro ao gerar certificação');
		}
	};

	const openChatCertificateEditor = () => {
		if (!chatCertDraft) return;
		if (!chatCertDraft.contactName.trim()) {
			toast.error('Informe o destinatário');
			return;
		}
		if (!chatCertMandadoType) {
			toast.error('Selecione um tipo de mandado');
			return;
		}
		if (!chatCertTemplateId) {
			toast.error('Selecione um modelo de certidão');
			return;
		}

		const mergedSentAt = mergeDateAndTime(
			chatCertSentDate,
			chatCertSentTime,
			chatCertCreatedAt || chatCertDraft.sentAt || chatCertDraft.createdAt || new Date().toISOString(),
		);
		const contactPayload = {
			contactName: chatCertDraft.contactName.trim(),
			contactPhone: chatCertDraft.contactPhone,
			contactEmail: chatCertDraft.contactEmail,
			contactDocument: chatCertDraft.contactDocument,
			contactAddress: chatCertDraft.contactAddress,
			contactProcess: chatCertDraft.contactProcess || chatCertDraft.mandateCaseNumber,
			contactMandate: chatCertMandadoType,
			contactCategory: chatCertDraft.contactCategory,
			contactNotes: chatCertDraft.contactNotes,
			mandateId: chatCertDraft.mandateId,
			mandateRecipientName: chatCertDraft.mandateRecipientName,
			mandateAddress: chatCertDraft.mandateAddress,
			mandateStatus: chatCertDraft.mandateStatus,
			mandateCaseNumber: chatCertDraft.mandateCaseNumber,
			mandateType: chatCertDraft.mandateType,
			sentAt: mergedSentAt,
			criadoEm: chatCertDraft.createdAt,
			conversationStartedAt: chatCertDraft.conversationStartedAt,
			conversationLastMessageAt: chatCertDraft.conversationLastMessageAt,
			conversationMessageCount: chatCertDraft.conversationMessageCount,
			conversationSummary: chatCertDraft.conversationSummary,
			conversationTranscript: chatCertDraft.conversationTranscript,
			campaignId: chatCertDraft.campaignId,
			campaignName: chatCertDraft.campaignName,
			campaignMessageId: chatCertDraft.campaignMessageId,
		};

		setShowChatCertSetupModal(false);
		setChatCertDraft(null);
		setChatCertMandadoType('');
		setChatCertTemplateId('');
		setChatCertSentDate('');
		setChatCertSentTime('');
		setChatCertCreatedAt(null);
		generateCertificate(chatCertTemplateId, contactPayload, chatCertMandadoType);
	};

	const generateCertificateWithAI = async (editedTemplateText: string, instructions?: string) => {
		const response = await authenticatedFetch('/campaigns/certificates/generate-ai', {
			method: 'POST',
			body: JSON.stringify({
				certificateText: editedTemplateText,
				editedTemplateText,
				instructions: instructions || '',
				templateTitle: certTemplate,
				mandadoType: certContext.mandadoType,
				contactName: certContext.contactName,
				contactPhone: certContext.contactPhone,
				contactEmail: certContext.contactEmail,
				contactDocument: certContext.contactDocument,
				contactAddress: certContext.contactAddress,
				contactProcess: certContext.contactProcess,
				contactMandate: certContext.contactMandate,
				contactCategory: certContext.contactCategory,
				dateText: certContext.dateText,
				timeText: certContext.timeText,
				sentAt: certContext.sentAt,
				conversationDateText: certContext.conversationDateText,
				conversationTimeText: certContext.conversationTimeText,
				conversationDateTimeText: certContext.conversationDateTimeText,
				conversationSummary: certContext.conversationSummary,
				conversationTranscript: certContext.conversationTranscript,
				conversationMessageCount: certContext.conversationMessageCount,
			}),
		});

		const data = await response.json().catch(() => ({}));

		if (!response.ok || !data?.success) {
			throw new Error(data?.message || 'Falha ao gerar certidão com IA');
		}

		if (data.provider === 'local') {
			const detail = data.warnings?.length
				? data.warnings.join('; ')
				: 'Nenhum provedor de IA configurado';
			throw new Error(`IA não disponível: ${detail}. Configure OpenAI, Groq, Claude ou Gemini nas Configurações.`);
		}

		return { text: data.text as string, provider: data.provider as string };
	};

	const sortedTemplates = [...templates].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

	return (
		<div className={embedded ? 'p-0' : 'p-6'}>
			{!embedded && (
			<div className="flex items-center justify-between mb-6">
				<h1 className="text-2xl font-semibold">Área de Certificação</h1>
			</div>
			)}

			<div className="mb-4 rounded-lg border border-amber-100 bg-amber-50 px-4 py-2 text-xs text-amber-900/80">
				Listas finalizadas serão excluídas automaticamente após 30 dias.
			</div>

			{showChatCertSetupModal && chatCertDraft && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
					<div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl">
						<div className="mb-4 flex items-start justify-between gap-4">
							<div>
								<p className="text-[11px] font-semibold uppercase tracking-wider text-green-600 mb-1">Certidão via Chat WhatsApp</p>
								<h2 className="text-base font-semibold text-gray-900">Conferir dados extraídos antes de gerar</h2>
								<p className="text-sm text-gray-500">{chatCertDraft.contactPhone || 'Telefone não informado'}</p>
							</div>
							<button
								type="button"
								onClick={() => {
									setShowChatCertSetupModal(false);
									setChatCertDraft(null);
									setChatCertMandadoType('');
									setChatCertTemplateId('');
									setChatCertSentDate('');
									setChatCertSentTime('');
									setChatCertCreatedAt(null);
									if (cameFromChatRef.current) {
										cameFromChatRef.current = false;
										navigate('/chats');
									}
								}}
								className="text-gray-400 hover:text-gray-600"
							>
								<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
							</button>
						</div>

						<div className="space-y-5">
							<div className="grid gap-4 md:grid-cols-2">
								<div className="md:col-span-2">
									<label className="mb-1 block text-sm font-medium text-gray-700">Destinatário</label>
									<input
										type="text"
										value={chatCertDraft.contactName}
										onChange={(event) => setChatCertDraft((current) => current ? {
											...current,
											contactName: event.target.value,
										} : current)}
										className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
										placeholder="Nome do destinatário"
									/>
								</div>

								<div>
									<label className="mb-1 block text-sm font-medium text-gray-700">Telefone</label>
									<input
										type="text"
										value={chatCertDraft.contactPhone || ''}
										onChange={(event) => setChatCertDraft((current) => current ? {
											...current,
											contactPhone: event.target.value,
										} : current)}
										className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
										placeholder="Telefone"
									/>
								</div>

								<div>
									<label className="mb-1 block text-sm font-medium text-gray-700">Modelo de certidão</label>
									<select
										value={chatCertTemplateId}
										onChange={(event) => setChatCertTemplateId(event.target.value)}
										className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
									>
										<option value="">Escolher modelo...</option>
										{sortedTemplates.map((template) => (
											<option key={template.id} value={template.id}>{formatTemplateDisplayTitle(template)}</option>
										))}
									</select>
								</div>
							</div>

							<div className="grid gap-4 md:grid-cols-3">
								<div>
								<label className="mb-1 block text-sm font-medium text-gray-700">Tipo de mandado</label>
								<select
									value={chatCertMandadoType}
									onChange={(event) => setChatCertMandadoType(event.target.value)}
									className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
								>
									<option value="">Selecionar tipo...</option>
									<option value="Citação">Citação</option>
									<option value="Intimação">Intimação</option>
									<option value="Citação/Intimação">Citação/Intimação</option>
									<option value="Notificação">Notificação</option>
									<option value="Convocação">Convocação</option>
									<option value="outro">Outro</option>
								</select>
								</div>

								<div>
									<label className="mb-1 block text-sm font-medium text-gray-700">Data do envio</label>
									<input
										type="date"
										value={chatCertSentDate}
										onChange={(event) => setChatCertSentDate(event.target.value)}
										className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
									/>
								</div>

								<div>
									<label className="mb-1 block text-sm font-medium text-gray-700">Hora do envio</label>
									<input
										type="time"
										value={chatCertSentTime}
										onChange={(event) => setChatCertSentTime(event.target.value)}
										className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
									/>
								</div>
							</div>

							<div className="grid gap-4 md:grid-cols-2">
								<div>
									<label className="mb-1 block text-sm font-medium text-gray-700">Processo</label>
									<input
										type="text"
										value={chatCertDraft.contactProcess || ''}
										onChange={(event) => setChatCertDraft((current) => current ? {
											...current,
											contactProcess: event.target.value,
										} : current)}
										className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
										placeholder="Número do processo"
									/>
								</div>

								<div>
									<label className="mb-1 block text-sm font-medium text-gray-700">Endereço</label>
									<input
										type="text"
										value={chatCertDraft.contactAddress || ''}
										onChange={(event) => setChatCertDraft((current) => current ? {
											...current,
											contactAddress: event.target.value,
										} : current)}
										className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
										placeholder="Endereço"
									/>
								</div>
							</div>

							{chatCertDraft.conversationSummary && (
								<div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
									<p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Resumo extraído da conversa</p>
									<p className="text-sm leading-6 text-slate-700">{chatCertDraft.conversationSummary}</p>
								</div>
							)}

							<button
								type="button"
								onClick={openChatCertificateEditor}
								disabled={!chatCertDraft.contactName.trim() || !chatCertMandadoType || !chatCertTemplateId}
								className={`w-full rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
									chatCertDraft.contactName.trim() && chatCertMandadoType && chatCertTemplateId
										? 'bg-green-600 text-white hover:bg-green-700'
										: 'cursor-not-allowed bg-gray-200 text-gray-400'
								}`}
							>
								Abrir certidão para edição
							</button>
						</div>
					</div>
				</div>
			)}

			<div className="mb-6 rounded-xl border border-blue-100/80 bg-blue-50/80 px-4 py-2.5 text-[10px] text-blue-900/70">
				<p className="text-[10px] font-normal uppercase tracking-wider text-blue-800/65">Variáveis disponíveis para os modelos de certidão</p>
				<p className="mt-1 leading-5">
					<code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{nome}}'}</code>
					<code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{destinatario}}'}</code>
					<code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{telefone}}'}</code>
					<code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{email}}'}</code>
					<code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{cpf}}'}</code>
					<code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{endereco}}'}</code>
					<code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{processo}}'}</code>
					<code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{mandado}}'}</code>
					<code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{ato}}'}</code>
					<code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{categoria}}'}</code>
					<code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{observacoes}}'}</code>
					<code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{data}}'}</code>
					<code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{data_conversa}}'}</code>
					<code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{hora_conversa}}'}</code>
					<code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{data_hora_conversa}}'}</code>
					<code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{resumo_conversa}}'}</code>
					<code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{historico_conversa}}'}</code>
					<code className="mx-1 rounded bg-blue-100/80 px-1 text-[10px] text-blue-900/75">{'{{tipo_de_mandado}}'}</code>
				</p>
			</div>

			<div className="mb-6">
				<div className="flex flex-wrap items-center gap-2">
					<button
						onClick={() => setActiveTab('certificar')}
						className={`px-4 py-2 rounded ${activeTab === 'certificar' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
					>
						Certificar Mandados
					</button>

					<button
						onClick={() => setActiveTab('modelos')}
						className={`px-4 py-2 rounded ${activeTab === 'modelos' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
					>
						Salvar Modelos
					</button>

					{activeTab === 'certificar' && (
						<button
							type="button"
							onClick={() => setIsCertificationImportModalOpen(true)}
							className="rounded bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
						>
							Importar para Certificação
						</button>
					)}
				</div>
			</div>

			{activeTab === 'certificar' && (
				<div className="p-4 bg-white rounded-lg shadow-sm">
					<div className="mb-4 flex items-center justify-between">
						<h2 className="text-lg font-medium">Ações de envio</h2>
					</div>

					{loadingCampaigns ? (
						<p>Carregando ações de envio...</p>
					) : (
						<>
							{!openedCampaign && (
								<>
									<ul className="space-y-2">
										{campaigns.map((c) => (
											<li key={c.id} className="flex items-center p-3 border rounded">
												<div className="flex-1 min-w-0">
													<p className="text-sm font-medium text-gray-900 truncate">{c.nome}</p>
													<p className="text-xs text-gray-500 mt-1">{c.criadoEm ? new Date(c.criadoEm).toLocaleString() : '-'}</p>
												</div>
												<div className="ml-4 flex-shrink-0 flex items-center gap-2">
													<button
														className="inline-flex items-center gap-1.5 rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50"
														onClick={() => openCampaign(c.id)}
														title="Certificar Mandados"
													>
														<svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
														</svg>
														<span>Certificar Mandados</span>
													</button>
													<button
														className="inline-flex items-center gap-1.5 rounded border border-red-200 bg-white px-3 py-1 text-sm font-medium text-red-700 hover:bg-red-50"
														onClick={() => handleDeleteCampaign(c.id)}
														title="Excluir lista"
													>
														<svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
															<path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
															<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
														</svg>
														Excluir lista
													</button>
												</div>
											</li>
										))}

										{campaigns.length === 0 && <li className="text-gray-500">Nenhuma ação de envio encontrada</li>}
									</ul>

									{hasOlderCampaigns && (
										<div className="mt-4 text-right">
											<button
												onClick={handleLoadOlderCampaigns}
												disabled={loadingOlderCampaigns}
												className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-60 disabled:cursor-not-allowed"
											>
												{loadingOlderCampaigns ? 'Carregando...' : 'Acessar Ações Mais antigas'}
											</button>
										</div>
									)}
								</>
							)}

							{openedCampaign && (
								<div className="mt-6 p-4 bg-gray-50 rounded-lg">
									<div className="flex items-center justify-between mb-2">
										<h3 className="text-lg font-medium">{openedCampaign.nome} - lista de mandados</h3>
										<div className="flex items-center gap-2">
											<button
												className="inline-flex items-center gap-1.5 rounded border border-red-200 bg-white px-3 py-1 text-sm font-medium text-red-700 hover:bg-red-50"
												onClick={() => handleDeleteCampaign(openedCampaign.id)}
												title="Excluir lista"
											>
												<svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
													<path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
													<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
												</svg>
												<span>Excluir lista</span>
											</button>
											<button
												className="text-sm text-blue-600 hover:text-blue-800 font-medium"
												onClick={() => {
													setOpenedCampaign(null);
													setOpenedMessages([]);
												}}
											>
												← Voltar
											</button>
										</div>
									</div>

									{loadingMessages ? (
										<p>Carregando...</p>
									) : (
										<div className="overflow-x-auto">
											<table className="min-w-full text-sm">
												<thead>
													<tr className="bg-gray-100">
														<th className="px-3 py-1 text-left">Nome</th>
														<th className="px-3 py-1 text-left">Data</th>
														<th className="px-3 py-1 text-left">Telefone</th>
														<th className="px-3 py-1 text-left">Positivo?</th>
														<th className="px-3 py-1 text-left">Tipo de Mandado</th>
														<th className="px-3 py-1 text-left">Escolher Modelo</th>
														<th className="px-3 py-1 text-left">Ação</th>
													</tr>
												</thead>
												<tbody>
													{openedMessages.map((msg) => (
														<tr key={msg.id} className="border-t">
															<td className="px-3 py-1">{msg.contactName}</td>
															<td className="px-3 py-1">{msg.criadoEm ? new Date(msg.criadoEm).toLocaleString() : msg.sentAt ? new Date(msg.sentAt).toLocaleString() : '-'}</td>
															<td className="px-3 py-1">{msg.contactPhone || '-'}</td>
															<td className="px-3 py-2">
																<div className="flex gap-1">
																	<button
																		onClick={() => msg.status !== 'COMPLETED' && setMessageStatus(msg, 'COMPLETED')}
																		className={`px-2 py-1 rounded text-xs font-medium ${
																			msg.status === 'COMPLETED' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
																		}`}
																	>
																		Sim
																	</button>
																	<button
																		onClick={() => msg.status !== 'PENDING' && setMessageStatus(msg, 'PENDING')}
																		className={`px-2 py-1 rounded text-xs font-medium ${
																			msg.status === 'PENDING' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
																		}`}
																	>
																		Não
																	</button>
																</div>
															</td>
															<td className="px-3 py-1">
																<select
																	value={mandadoTypeByMsg[msg.id] || ''}
																	onChange={(e) => setMandadoTypeByMsg((old) => ({ ...old, [msg.id]: e.target.value }))}
																	className="border rounded px-2 py-1 text-xs text-gray-700"
																>
																	<option value="" disabled>Selecionar Tipo...</option>
																	<option value="Citação">Citação</option>
																	<option value="Intimação">Intimação</option>
																	<option value="Citação/Intimação">Citação/Intimação</option>
																	<option value="Notificação">Notificação</option>
																	<option value="Convocação">Convocação</option>
																	<option value="outro">Outro</option>
																</select>
															</td>
															<td className="px-3 py-1">
																<select
																	value={selectedTemplateByMsg[msg.id] || ''}
																	onChange={(e) => setSelectedTemplateByMsg((old) => ({ ...old, [msg.id]: e.target.value }))}
																	className="border rounded px-2 py-1 text-xs text-gray-700"
																>
																	<option value="" disabled>Escolher Modelo de Certidão...</option>
																	{sortedTemplates.map((t) => (
																		<option key={t.id} value={t.id}>{formatTemplateDisplayTitle(t)}</option>
																	))}
																</select>
															</td>
															<td className="px-3 py-1">
																<button
																	disabled={!selectedTemplateByMsg[msg.id] || !mandadoTypeByMsg[msg.id]}
																	onClick={() => {
																		const selectedId = selectedTemplateByMsg[msg.id];
																		if (!selectedId) return toast.error('Selecione um modelo primeiro');
																		if (!mandadoTypeByMsg[msg.id]) return toast.error('Selecione um tipo de mandado primeiro');
																		generateCertificate(selectedId, msg, mandadoTypeByMsg[msg.id]);
																	}}
																	className={`px-3 py-1 rounded text-xs font-medium ${
																		selectedTemplateByMsg[msg.id] && mandadoTypeByMsg[msg.id]
																			? 'bg-green-600 text-white hover:bg-green-700 cursor-pointer'
																			: 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
																	}`}
																>
																	Gerar Certidão
																</button>
															</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									)}
								</div>
							)}
						</>
					)}
				</div>
			)}

			{activeTab === 'modelos' && (
				<div className="p-4 bg-white rounded-lg shadow-sm">
					<div className="flex items-center justify-between mb-4">
						<h2 className="text-lg font-medium">Modelos de Certidão</h2>
						<button
							onClick={() => {
								setEditing(null);
								setModalTitle('');
								setModalBody('');
								setShowTemplateModal(true);
							}}
							className="px-3 py-1 bg-green-600 text-white rounded text-sm"
						>
							+ Adicionar modelo
						</button>
					</div>

					{templates.length === 0 ? (
						<p className="text-gray-500">Nenhum modelo salvo.</p>
					) : (
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
							{sortedTemplates.map((t) => (
								<div key={t.id} className="border rounded p-4 flex flex-col justify-between">
									<div>
										<h3 className="font-semibold text-sm text-gray-800">{formatTemplateDisplayTitle(t)}</h3>
										<p className="text-xs text-gray-600 mt-2 whitespace-pre-wrap" style={{ maxHeight: '6rem', overflow: 'hidden' }}>
											{t.body}
										</p>
									</div>
									<div className="mt-4 flex justify-end gap-2">
										<button
											onClick={() => {
												setEditing(t);
												setModalTitle(stripTemplateSequenceLabel(t.title));
												setModalBody(t.body);
												setShowTemplateModal(true);
											}}
											className="text-blue-600 text-sm"
										>
											Editar
										</button>
										<button
											onClick={() => {
												if (window.confirm('Apagar este modelo?')) {
													setTemplates((old) => resequenceTemplates(old.filter((x) => x.id !== t.id)));
												}
											}}
											className="text-red-600 text-sm"
										>
											Apagar
										</button>
									</div>
								</div>
							))}
						</div>
					)}

					{showTemplateModal && (
						<div className="fixed inset-0 bg-gray-800 bg-opacity-40 flex items-center justify-center z-40">
							<div className="bg-white p-6 rounded shadow-lg w-full max-w-lg">
								<h3 className="text-xl font-semibold mb-4">{editing ? 'Editar modelo' : 'Novo modelo'}</h3>
								<div className="mb-3">
									<label className="block text-sm font-medium text-gray-700">Título</label>
									<input
										type="text"
										className="mt-1 block w-full border rounded px-2 py-1"
										value={modalTitle}
										onChange={(e) => setModalTitle(e.target.value)}
									/>
								</div>
								<div className="mb-3">
									<label className="block text-sm font-medium text-gray-700">Texto</label>
									<textarea
										rows={8}
										className="mt-1 block w-full border rounded px-2 py-1"
										value={modalBody}
										onChange={(e) => setModalBody(e.target.value)}
									/>
								</div>
								<div className="flex justify-end gap-2">
									<button onClick={() => setShowTemplateModal(false)} className="px-3 py-1 bg-gray-300 rounded text-sm">Cancelar</button>
									<button
										onClick={() => {
											const id = editing ? editing.id : Date.now().toString();
											const newModel = { id, title: stripTemplateSequenceLabel(modalTitle), body: modalBody };
											setTemplates((old) => {
												if (editing) return resequenceTemplates(old.map((x) => (x.id === id ? newModel : x)));
												return resequenceTemplates([...old, newModel]);
											});
											setShowTemplateModal(false);
										}}
										className="px-3 py-1 bg-blue-600 text-white rounded text-sm"
									>
										Salvar
									</button>
								</div>
							</div>
						</div>
					)}
				</div>
			)}

			{showCertModal && (
				<GenerateCertificateModal
					isOpen={showCertModal}
					certificateText={certText}
					templateTitle={certTemplate}
					recipientName={certContext.contactName}
					onGenerateWithAI={generateCertificateWithAI}
					onClose={() => {
						setShowCertModal(false);
						if (cameFromChatRef.current) {
							cameFromChatRef.current = false;
							navigate('/chats');
						}
					}}
				/>
			)}

			<CertificationImportModal
				isOpen={isCertificationImportModalOpen}
				onClose={() => setIsCertificationImportModalOpen(false)}
				onSuccess={handleCertificationImportSuccess}
			/>
		</div>
	);
};

